import React from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Header from './Header';
import { Loader2, DownloadCloud } from 'lucide-react';

const AppLayout: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [isMounted, setIsMounted] = React.useState(false);

  // Redirect to login page if not authenticated
  React.useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Set mounted state to true on initial mount to help with transitions
  React.useEffect(() => {
    // Use a small timeout to ensure the component is fully mounted before animation
    const timer = setTimeout(() => {
      setIsMounted(true);
    }, 50);
    
    // Clean up function
    return () => {
      clearTimeout(timer);
      setIsMounted(false);
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-background to-muted/30">
      <Header />
      
      {isLoading ? (
        <div className={`flex-1 flex items-center justify-center transition-opacity duration-500 ${isMounted ? 'opacity-100' : 'opacity-0'}`}>
          <div className="flex flex-col items-center">
            <div className="relative">
              <Loader2 className="h-12 w-12 text-primary animate-spin" />
              <div className="absolute inset-0 flex items-center justify-center">
                <DownloadCloud className="h-6 w-6 text-primary/70" />
              </div>
            </div>
            <p className="mt-4 text-lg text-muted-foreground">Loading your attachments...</p>
          </div>
        </div>
      ) : (
        <main 
          className={`flex-1 w-full px-4 sm:px-6 lg:px-8 py-6 sm:py-8 transition-all duration-500 ${
            isMounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <div className="max-w-[1400px] mx-auto">
            {children || <Outlet />}
          </div>
        </main>
      )}
      
      <footer className="py-4 sm:py-6 border-t border-border/60 bg-gradient-to-r from-background to-secondary/20">
        <div className="px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center gap-2">
          <div className="flex items-center gap-2">
            <DownloadCloud className="h-4 w-4 text-primary/70" />
            <span className="text-sm font-medium bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
              Attachment Panda
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            &copy; {new Date().getFullYear()} All rights reserved
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AppLayout;
